import clsx from 'clsx';
import { FocusEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import TextField from '../TextField';

import { AutoCompleteSelectItem } from './AutocompleteSelectItem';
import { DropdownIndicator } from './DropdownIndicator';
import { AutocompleteOptionProps, AutocompleteSelectProps, maskRegex } from './types';
import styles from './Autocomplete.module.css';

const AutocompleteSelect = ({
  options,
  value,
  name,
  mask,
  masked,
  placeholder,
  className,
  size = 'medium',
  variant = 'rounded',
  onChange,
  isDisabled,
  noOptionText,
  ...rest
}: AutocompleteSelectProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState(0);
  const [inputValue, setInputValue] = useState<string | number>('');
  const autoCompleteInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isVisible) {
      if (value && value.value === '') {
        return;
      }
    } else if (value) {
      setInputValue(value.label);
    }
  }, [value]);

  const searchContainer = useRef<any>(null);
  const searchResultRef = useRef<any>(null);

  const suggestions = useMemo(() => {
    if (!search) return options;

    setCursor(0);

    return options.filter((item: AutocompleteOptionProps) =>
      item.value.toString().toLowerCase().includes(search.toString().toLowerCase()),
    );
  }, [options, search]);

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const showSuggestion = () => setIsVisible(true);

  const hideSuggestion = () => {
    setIsVisible(false);
  };

  const handleSelect = (option: AutocompleteOptionProps) => {
    setSearch(option.label.toString());
    setInputValue(option.label);
    onChange?.(option);
    hideSuggestion();
    setSearch('');
    autoCompleteInputRef.current?.blur();
  };

  const unmask = (val: string) => parseInt(val).toString().replace(new RegExp(/[^\d]/, 'g'), ''); // Remove every non-digit character

  const handleChange = (event: any) => {
    const element = event.currentTarget;
    let newValue = element.value.toString();

    if (masked) {
      newValue = unmask(newValue);
      if (mask && !newValue.match(maskRegex[mask])) {
        return newValue;
      }
    }

    setSearch(newValue);

    if (typeof event.nativeEvent.inputType === 'undefined') {
      handleSelect({ label: newValue, value: newValue });
    } else {
      setInputValue(newValue);
    }
    setCursor(-1);
  };

  const handleFocus = ({ target }: any) => {
    showSuggestion();
    target?.select();
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const element = event.currentTarget;
    if (element.value !== '' && masked) {
      if (search === '') return;

      handleSelect({ label: element.value, value: element.value });
      return;
    }

    if (element.value !== '' && value) {
      setInputValue(value.label);
      return;
    }
    if (element.value === '' && value) {
      setInputValue(value.label);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (searchContainer.current && !searchContainer.current.contains(event.target)) {
      hideSuggestion();
    }
  };

  const keyboardNavigation = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      isVisible ? setCursor((cur) => (cur < suggestions.length - 1 ? cur + 1 : cur)) : showSuggestion();
    }

    if (event.key === 'ArrowUp') {
      setCursor((cur) => (cur > 0 ? cur - 1 : 0));
    }

    if (event.key === 'Escape') {
      hideSuggestion();
    }

    if (event.key === 'Enter' && cursor >= 0) {
      handleSelect(suggestions[cursor]);
    }
  };

  return (
    <div className={styles.autocompleteInputContainer} ref={searchContainer}>
      <TextField
        name={name}
        data-testid={'autocomplete-field'}
        value={inputValue.toString()}
        placeholder={placeholder || ''}
        onChange={handleChange}
        onClick={showSuggestion}
        onKeyDown={keyboardNavigation}
        onFocus={handleFocus}
        className={className}
        inputClasses={`${styles.autocompleteInputParent} autocompleteBase`}
        inputClassName={styles.autocompleteInput}
        variant={variant}
        size={size}
        disabled={isDisabled}
        endAdornment={
          <>
            <DropdownIndicator
              data-testid="autoselect-down-icon"
              disabled={isDisabled}
              showOptions={isVisible}
              onClick={(event: any) => {
                const currentInput = event?.target.closest('div').getElementsByTagName('input');
                currentInput?.[0].focus();
                setIsVisible(!isVisible);
              }}
            />

            {isVisible && (
              <ul
                data-testid="autoselect-list"
                className={clsx(styles.autoselectSuggestion, {
                  [styles.smallInput]: size === 'small',
                  [styles.mediumInput]: size === 'medium',
                })}
                ref={searchResultRef}
              >
                {suggestions.length > 0 ? (
                  suggestions.map((option: any, idx: number) => (
                    <AutoCompleteSelectItem
                      key={`${idx}${option}`}
                      option={option}
                      onSelectItem={() => {
                        handleSelect(option);
                      }}
                      isActive={value && option.value.toString().toLowerCase() === value.value.toString().toLowerCase()}
                      isHighlighted={cursor === idx}
                    />
                  ))
                ) : (
                  <li className="px-4 py-2 text-gray-500">{noOptionText || 'No results'}</li>
                )}
              </ul>
            )}
          </>
        }
        {...rest}
        ref={autoCompleteInputRef}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default AutocompleteSelect;
